import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AlertTriangle, Wrench, ListChecks, Camera, Pencil, CornerDownRight, Images } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuthedImage } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";

export default function KnowledgeDetail() {
  const { case_id } = useParams();
  const [kc, setKc] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get(`/knowledge/${case_id}`).then(({ data }) => setKc(data)); }, [case_id]);
  if (!kc) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">{kc.category}</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl">{kc.title}</h1>
        <p className="mt-4 text-muted-foreground">{kc.problem}</p>

        {kc.warnings?.length > 0 && (
          <div className="mt-6 border-l-4 border-[color:hsl(var(--amber))] bg-[color:hsl(var(--amber))]/10 rounded-r-xl p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[color:hsl(var(--amber))]"><AlertTriangle className="w-4 h-4" /> Warnings</div>
            <ul className="mt-2 text-sm space-y-1 list-disc pl-5">
              {kc.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {user && kc.visual_recap?.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--amber))]">
              <Images className="w-4 h-4" /> Visual recap ({kc.visual_recap.length} step{kc.visual_recap.length > 1 ? "s" : ""})
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Auto-stitched from the photos, mark-ups and follow-ups shared in the live Help Room.</p>
            <ol className="mt-4 space-y-4">
              {kc.visual_recap.map((s) => <RecapStep key={s.step} step={s} />)}
            </ol>
          </div>
        )}
        {!user && kc.visual_recap?.length > 0 && (
          <div className="mt-8 border rounded-2xl bg-[color:hsl(var(--amber))]/5 border-[color:hsl(var(--amber))]/30 p-4 flex items-center gap-3">
            <Images className="w-4 h-4 text-[color:hsl(var(--amber))] shrink-0" />
            <div className="text-sm">
              This solution has a <b>{kc.visual_recap.length}-step visual walkthrough</b>.
              {" "}<Link to="/login" className="text-[color:hsl(var(--teal))] font-medium underline underline-offset-2">Sign in to view the photos</Link>.
            </div>
          </div>
        )}

        <div className="mt-6 border rounded-2xl bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><ListChecks className="w-4 h-4" /> Steps</div>
          <ol className="mt-3 space-y-2 list-decimal pl-5 text-sm">
            {kc.steps?.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>

        {kc.tools?.length > 0 && (
          <div className="mt-4 border rounded-2xl bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><Wrench className="w-4 h-4" /> Tools</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {kc.tools.map((t) => <span key={t} className="text-xs rounded-full bg-secondary px-3 py-1 border">{t}</span>)}
            </div>
          </div>
        )}

        <div className="mt-4 border rounded-2xl bg-card p-5">
          <div className="text-sm font-mono-label text-[color:hsl(var(--amber))]">Solution</div>
          <p className="mt-2 text-sm">{kc.solution}</p>
        </div>

        {kc.contributor_details?.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-mono-label text-muted-foreground">Contributed by</div>
            <div className="mt-3 flex flex-wrap gap-3">
              {kc.contributor_details.map((c) => (
                <Link key={c.user_id} to={`/u/${c.user_id}`} className="flex items-center gap-2 border rounded-full bg-card pr-3 py-1 pl-1 hover:border-[color:hsl(var(--teal))]">
                  <Avatar className="h-6 w-6"><AvatarImage src={c.picture} /><AvatarFallback className="text-[10px] bg-[color:hsl(var(--teal))] text-white">{c.name?.[0]}</AvatarFallback></Avatar>
                  <span className="text-sm">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecapStep({ step }) {
  const src = useAuthedImage(step.image_path);
  const Icon = step.kind === "annotation" ? Pencil : step.reply_to ? CornerDownRight : Camera;
  const iconColor = step.kind === "annotation" ? "text-[color:hsl(var(--amber))]" : step.reply_to ? "text-[color:hsl(var(--teal))]" : "text-muted-foreground";
  return (
    <li className="flex gap-4 border rounded-2xl bg-card p-4" data-testid={`recap-step-${step.step}`}>
      <div className="shrink-0 grid place-items-center w-8 h-8 rounded-full bg-[color:hsl(var(--teal))] text-white font-display font-bold text-sm">
        {step.step}
      </div>
      <div className="shrink-0">
        {src ? (
          <img src={src} alt={`Step ${step.step}: ${step.caption}`} className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-xl border" />
        ) : (
          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-secondary animate-pulse" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`inline-flex items-center gap-1 text-[10px] font-mono-label ${iconColor}`}>
          <Icon className="w-3 h-3" />
          {step.kind === "annotation" ? "Marked up" : step.reply_to ? "Follow-up" : "Photo"}
          {step.sender_name ? ` · ${step.sender_name}` : ""}
        </div>
        <p className="mt-1 text-sm leading-relaxed">{step.caption}</p>
      </div>
    </li>
  );
}
